import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { InfoCircleOutlined } from '@ant-design/icons';

import type { MyPlanCardDto } from '../../lib/api/openingTasks';
import { useOpeningTasks } from '../opening-tasks/OpeningTasksSection';
import { TrajectoryPanel } from '../opening-tasks/SidePanels';
import { UkResultsPanel } from '../opening-tasks/UkResultsPanel';
import { PlanFeed } from './PlanFeed';
import { PlanPhaseStrip } from './PlanPhaseStrip';
import { PlanProperties } from './PlanProperties';
import { PlanStageList } from './PlanStageList';
import { PLAN_COPY, type PlanMode } from './plan-model';

/**
 * Плашка про текущий этап: задачи этапа и заявка на переход живут ТОЛЬКО в «Моих задачах» (владелец 31.10.2026),
 * визард ведёт туда ссылкой. Показываем, пока открытие идёт.
 */
function CurrentStageNote({ card }: { card: MyPlanCardDto }): JSX.Element | null {
  if (card.statusV2 !== 'active') return null;
  return (
    <div className="fk-ot-callout" data-tone="info">
      <InfoCircleOutlined />
      <div style={{ minWidth: 0 }}>
        Сейчас этап «<b>{card.stage.label}</b>». Задачи этапа
        {card.nextStage ? (
          <>
            {' '}
            и заявка на переход в «{card.nextStage.label}»
          </>
        ) : null}{' '}
        — в разделе «<Link to="/tasks" className="fk-ot-more-link">Мои задачи</Link>».
      </div>
    </div>
  );
}

/** Траектория обучения — зрителя, поэтому только у партнёра (приходит из «Моих задач»; нет данных — не показываем). */
function PartnerTrajectory(): JSX.Element | null {
  const trajectory = useOpeningTasks().query.data?.trajectory ?? [];
  return trajectory.length > 0 ? <TrajectoryPanel groups={trajectory} /> : null;
}

/**
 * Вкладка «Обзор»: полосы фаз, плашка про текущий этап, список этапов, лента событий; справа — свойства, результаты
 * УК и траектория обучения. У менеджера (`mode="manager"`) плашки «в разделе Мои задачи» и траектории нет — обе про
 * самого зрителя, а не про партнёра, и запрос «Моих задач» не делается.
 */
export function PlanOverviewTab({
  card,
  onGoStage,
  mode = 'partner',
}: {
  card: MyPlanCardDto;
  onGoStage: (stageCode: string) => void;
  mode?: PlanMode;
}): JSX.Element {
  const partner = mode === 'partner';
  return (
    <div className="fk-op-ov">
      <div className="fk-op-panel">
        <PlanPhaseStrip card={card} />
        {partner ? <CurrentStageNote card={card} /> : null}
        <PlanStageList card={card} onGoStage={onGoStage} />
        <PlanFeed events={card.events} />
      </div>
      <div className="fk-op-panel">
        <PlanProperties card={card} />
        {card.ukResults.length > 0 ? <UkResultsPanel results={card.ukResults} sub={PLAN_COPY[mode].ukResultsSub} /> : null}
        {partner ? <PartnerTrajectory /> : null}
      </div>
    </div>
  );
}
